import { animate, style, transition, trigger } from '@angular/animations'
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'
import { Permission } from '@employer/app/config/permission.config'
import { EmployerNavigationService, ROUTES } from '@employer/app/services/navigation.service'
import { AssistantOnboardingService } from '@employer/app/modules/assistant/services/assistant-onboarding.service'
import { PageTitleService } from '@employer/app/services/page-title.service'
import { SidebarPreferencesService } from '@employer/app/services/sidebar-preferences.service'
import { isDesktopTier, isMobileTier, isTabletTier, NAV_BREAKPOINTS, NavTier, navTierFromWidth } from '@employer/app/shared/nav-breakpoints'
import { PermissionStore } from '@engineering11/access-web'

import { AppConfigService } from '@engineering11/tenant-bootstrap-web'
import { INavBottomTab } from '@engineering11/ui-lib/e11-nav-left'
import { E11SideOverContainerComponent } from '@engineering11/ui-lib/e11-side-over'
import { ViewportService } from '@engineering11/web-utilities'
import { Store } from '@ngrx/store'
import { Observable, combineLatest, fromEvent, map, shareReplay, tap } from 'rxjs'
import { filter, startWith } from 'rxjs/operators'
import { ConfigStore, userActions } from 'shared-lib'
import { ExtendedNavId } from './main-layout.interface'

@Component({
  selector: 'app-main-layout',
  template: `
    <sdk-company-session-timeout (onTimeout)="logout()"></sdk-company-session-timeout>
    <ng-container *ngrxLet="extendedNavContent$ as extendedNavContent">
      <ng-container *ngrxLet="isMobile$ as isMobile">
        <ng-container *ngrxLet="userPermissions$ as permissions">
          <ng-container *ngrxLet="navTier$ as navTier">
            <ng-container *ngrxLet="sidebarCollapsed$ as collapsedPref">
              <div class="e11-h-screen e11-overflow-hidden e11-flex e11-flex-col">
                <app-impersonation-banner></app-impersonation-banner>
                <app-client-switch-banner></app-client-switch-banner>
                <div class="e11-flex-1 e11-min-h-0 e11-flex e11-overflow-hidden e11-transition-all e11-duration-300">
                  <div class="e11-flex">
                    <nav
                      #primaryNav
                      class="e11-hidden lg:e11-flex lg:e11-gap-4 e11-h-full e11-flex-col e11-bg-skin-app-bg-dark e11-relative"
                      aria-label="Primary"
                      [ngClass]="{
                        'lg:e11-w-[290px] e11-p-6': !shouldRenderCollapsed(collapsedPref, extendedNavContent),
                        'lg:e11-w-20 e11-p-4': shouldRenderCollapsed(collapsedPref, extendedNavContent)
                      }"
                    >
                      <div
                        class="e11-flex e11-items-center"
                        [ngClass]="{
                          'e11-justify-between': !shouldRenderCollapsed(collapsedPref, extendedNavContent),
                          'e11-flex-col e11-gap-3': shouldRenderCollapsed(collapsedPref, extendedNavContent)
                        }"
                      >
                        <a
                          aria-label="Cnect home"
                          class="e11-hidden e11-header-logo lg:e11-flex e11-items-center e11-justify-center e11-cursor-pointer"
                          [routerLink]="permissions?.has(Permission.ViewJobs) ? ROUTES.JOBS : ROUTES.CLIENT_MANAGEMENT"
                        >
                          <img
                            [src]="shouldRenderCollapsed(collapsedPref, extendedNavContent) ? collapsedLogoUrl : logoUrl"
                            class="e11-object-contain"
                            [ngClass]="{
                              'e11-max-h-10 e11-w-10': shouldRenderCollapsed(collapsedPref, extendedNavContent),
                              'e11-max-h-24': !shouldRenderCollapsed(collapsedPref, extendedNavContent)
                            }"
                            alt=""
                          />
                        </a>
                        <button
                          type="button"
                          class="e11-w-9 e11-h-9 e11-rounded e11-flex e11-items-center e11-justify-center e11-text-skin-white hover:e11-bg-skin-white/15 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-skin-white/60 e11-shrink-0"
                          [attr.aria-label]="shouldRenderCollapsed(collapsedPref, extendedNavContent) ? 'Expand sidebar' : 'Collapse sidebar'"
                          [title]="shouldRenderCollapsed(collapsedPref, extendedNavContent) ? 'Expand sidebar' : 'Collapse sidebar'"
                          (click)="toggleCollapsed(navTier)"
                        >
                          <span class="material-symbols-outlined">{{
                            shouldRenderCollapsed(collapsedPref, extendedNavContent) ? 'menu' : 'menu_open'
                          }}</span>
                        </button>
                      </div>
                      <ng-container *ngrxLet="tabsConfig$ as tabsConfig">
                        <!-- Expanded rail: app-owned menu (one consistent click contract; see nav/ components) -->
                        <app-nav-menu
                          *ngIf="!shouldRenderCollapsed(collapsedPref, extendedNavContent)"
                          class="e11-flex-grow e11-min-h-0 e11-overflow-y-auto e11-border-t e11-border-skin-grey/30 e11-pt-2"
                          [items]="tabsConfig"
                          [customTemplates]="{ message: messageSlot }"
                        ></app-nav-menu>
                        <!-- Collapsed rail: icon buttons; group icons open a flyout (children stay reachable) -->
                        <app-nav-icon-rail
                          *ngIf="shouldRenderCollapsed(collapsedPref, extendedNavContent)"
                          class="e11-flex-grow e11-min-h-0 e11-border-t e11-border-skin-grey/30 e11-pt-2"
                          [items]="tabsConfig"
                          [customTemplates]="{ message: messageSlotIconOnly }"
                        ></app-nav-icon-rail>
                      </ng-container>
                    </nav>
                    <ng-container *ngrxLet="extendedNavContent$ as extendedNavContent">
                      <div
                        [@slideInOut]
                        *ngIf="extendedNavContent && isDesktopTier(navTier)"
                        class="e11-hidden lg:e11-flex e11-w-80 e11-h-full e11-overflow-y-auto e11-flex-col"
                        [ngClass]="extendedNavClasses(extendedNavContent)"
                      >
                        <messaging-extended-nav *ngIf="extendedNavContent === ExtendedNavId.Messaging" />
                        <reports-extended-nav *ngIf="extendedNavContent === ExtendedNavId.Reports" />
                        <settings-extended-nav *ngIf="extendedNavContent === ExtendedNavId.Settings" />
                      </div>
                    </ng-container>
                  </div>

                  <main class="e11-flex-1 e11-flex-col e11-min-w-0 e11-h-screen e11-overflow-hidden e11-relative">
                    <app-header [mobileDrawerState]="mobileDrawerState" (mobileDrawerStateChange)="mobileDrawerStateChange($event)">
                      <!-- app-header renders the title itself (and the breadcrumb trail with it);
                           it projects no content, so nothing placed here would appear. -->
                    </app-header>

                    <!-- Settings rail below 'lg' (CNCT-6779). The desktop slot above is a flex sibling of
                         the menu, but the menu is off-canvas at these widths, so the rail moves inside the
                         content region: a docked left column on tablet, and a dropdown under the top bar on
                         mobile with a trigger row naming the open category. Both are laid out in flow — the
                         dropdown anchors to its own trigger rather than to a hard-coded header height, which
                         varies with the header's content. Only one 'settings-nav' ever exists: the two slots
                         are mutually exclusive on tier. -->
                    <div
                      *ngIf="extendedNavContent === ExtendedNavId.Settings && isMobileTier(navTier)"
                      class="settings-rail-mobile e11-relative e11-shrink-0"
                    >
                      <button
                        type="button"
                        class="settings-rail-trigger e11-flex e11-items-center e11-gap-2 e11-w-full e11-h-11 e11-px-4 e11-text-left e11-text-sm e11-bg-skin-white e11-border-0"
                        [attr.aria-expanded]="settingsRailOpen"
                        aria-controls="settings-rail-dropdown"
                        (click)="toggleSettingsRail()"
                      >
                        <span class="material-symbols-outlined md-18">settings</span>
                        <span class="e11-flex-1 e11-truncate">
                          <span class="e11-font-primary-medium e11-text-skin-dark">{{ 'Settings' | translate }}</span>
                          <ng-container *ngIf="settingsRailCategory$ | async as category">
                            <span class="settings-rail-sep e11-text-skin-light">&bull;</span>{{ category | translate }}
                          </ng-container>
                        </span>
                        <span class="material-symbols-outlined md-20 settings-rail-chev">expand_more</span>
                      </button>

                      <ng-container *ngIf="settingsRailOpen">
                        <div class="settings-rail-scrim" (click)="closeSettingsRail()"></div>
                        <div id="settings-rail-dropdown" class="settings-rail-dropdown e11-flex e11-flex-col e11-bg-skin-white">
                          <settings-extended-nav />
                        </div>
                      </ng-container>
                    </div>

                    <div class="e11-flex e11-flex-1 e11-min-h-0">
                      <div
                        *ngIf="extendedNavContent === ExtendedNavId.Settings && isTabletTier(navTier)"
                        class="settings-rail-docked e11-shrink-0 e11-flex e11-flex-col e11-overflow-y-auto e11-bg-skin-white"
                      >
                        <settings-extended-nav />
                      </div>

                      <div
                        #mainLayoutScrollableElement
                        class="e11-flex e11-flex-col e11-flex-1 e11-min-w-0 e11-min-h-0 e11-overflow-y-scroll e11-overflow-x-hidden e11-px-4 e11-pt-4 md:e11-px-6 md:e11-pt-6 e11-pb-16"
                        [ngClass]="{ 'e11-pb-[72px]': isMobile }"
                      >
                        <!-- The content gutter for EVERY route: the only padding between a screen and
                             the app chrome. 16px below md (768px, the mobile tier's ceiling), 24px from
                             there up. The BOTTOM is not part of that pair on purpose — pb-16, and
                             pb-[72px] on mobile, is what clears the fixed bottom tab bar (h-14 plus the
                             safe-area inset), so shrinking it would put the footer under the bar. -->
                        <router-outlet></router-outlet>
                        <!-- Footer stays in normal flow (scrolls with content, zero permanent height). The
                         persistent bar is a flex sibling BELOW this scroll area, so it never covers the
                         footer — no need to duplicate it under the bar. -->
                        <app-footer class="e11-mt-auto"></app-footer>
                      </div>
                    </div>
                    <nav
                      *ngIf="isMobile"
                      aria-label="Quick navigation"
                      class="e11-fixed e11-bottom-0 e11-inset-x-0 e11-h-14 e11-bg-skin-white e11-border-t e11-border-skin-grey/20 e11-flex e11-z-40"
                      [style.paddingBottom]="'env(safe-area-inset-bottom)'"
                    >
                      <button
                        *ngFor="let tab of mobileTabs$ | async"
                        type="button"
                        class="e11-flex-1 e11-flex e11-flex-col e11-items-center e11-justify-center e11-py-1 e11-min-h-[44px] focus:e11-outline-none focus:e11-bg-skin-primary/5"
                        [class.e11-text-skin-primary-accent]="isMobileTabActive(tab)"
                        [class.e11-text-skin-grey]="!isMobileTabActive(tab)"
                        (click)="onMobileTabTap(tab)"
                        [attr.aria-current]="isMobileTabActive(tab) ? 'page' : null"
                      >
                        <span class="material-symbols-outlined e11-text-xl">{{ tab.icon }}</span>
                        <span class="e11-text-xs">{{ tab.name }}</span>
                      </button>
                    </nav>

                    <!-- Assistant (UX spec: .foundry-output/features/cnect-assistant/ux-command-bar-spec.md).
                       Collapsed → a small launcher FAB (fixed corner, ~zero body footprint). Expanded → the
                       full-width resizable terminal panel: conversation on-screen with the composer
                       full-width at its bottom (Claude-Code-terminal style), as a bottom flex row so it
                       takes space from the content rather than overlaying it. Desktop-only. -->
                    <ng-container *ngIf="assistantEnabled$ | async">
                      <div *ngIf="assistantBarExpanded$ | async" class="e11-hidden lg:e11-flex e11-flex-col e11-shrink-0 e11-px-6 e11-pb-2 e11-pt-1">
                        <assistant-terminal class="e11-w-full e11-block"></assistant-terminal>
                      </div>

                      <!-- Collapsed launcher — near-zero footprint; fixed bottom-right, opens the assistant (⌘K). -->
                      <button
                        *ngIf="!(assistantBarExpanded$ | async)"
                        type="button"
                        (click)="expandAssistantBar()"
                        class="e11-hidden lg:e11-flex e11-fixed e11-bottom-6 e11-right-6 e11-z-30 e11-w-12 e11-h-12 e11-rounded-full e11-bg-skin-white e11-border e11-border-skin-app-borders e11-shadow-lg e11-items-center e11-justify-center hover:e11-border-skin-primary-accent focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-skin-primary-accent/60"
                        [attr.aria-label]="'Ask the assistant' | translate"
                        [title]="'Ask the assistant (⌘K)' | translate"
                      >
                        <span class="material-icons-outlined md-24 e11-text-skin-primary-accent">auto_awesome</span>
                      </button>
                    </ng-container>
                  </main>
                  <!-- The tour surfaces ship on their own, deliberately OUTSIDE the assistant dark-launch
                     flag: the floating guided-walkthrough card and the anchored indicator layer that
                     rings its subject (both render only while the guide is active), plus the
                     first-login tour offer card (entitlement-gated in its service). Only the
                     assistant's client-tool highlight spotlight stays behind the flag. -->
                  <tour-indicator></tour-indicator>
                  <tour-card></tour-card>
                  <assistant-onboarding-offer></assistant-onboarding-offer>
                  <ng-container *ngIf="assistantEnabled$ | async">
                    <assistant-highlight-overlay></assistant-highlight-overlay>
                  </ng-container>
                </div>
              </div>
            </ng-container>
          </ng-container>
        </ng-container>
        <e11-side-over-container
          #mobileNavSideOver
          [size]="'lg'"
          [sideOverPosition]="'left'"
          [containerClassOverrides]="'e11-bg-transparent e11-w-3/4'"
        >
          <ng-template e11Template="content">
            <div
              data-nav-drawer-panel
              class="e11-bg-skin-app-bg-dark e11-h-full e11-flex e11-flex-col e11-shadow-xl"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation"
            >
              <img [src]="logoUrl" class="e11-h-16 e11-max-h-16 e11-object-contain e11-object-left e11-px-6" alt="Cnect" />
              <!-- One nav-menu instance = the full menu rendered exactly once (fixes the 4x
                   duplication from e11-nav-left's per-tab drawer loop). A leaf tap closes the
                   drawer; the router-event subscription in the constructor backstops it. -->
              <ng-container *ngrxLet="tabsConfig$ as tabsConfig">
                <app-nav-menu
                  class="e11-flex-grow e11-min-h-0 e11-overflow-y-auto e11-px-4 e11-py-2 e11-border-t e11-border-skin-grey/30"
                  ariaLabel="Navigation"
                  [items]="tabsConfig"
                  [customTemplates]="{ message: messageSlot }"
                  (leafNavigated)="mobileDrawerStateChange(false)"
                ></app-nav-menu>
              </ng-container>
            </div>
          </ng-template>
        </e11-side-over-container>

        <!-- Messages nav item (custom slot) — desktop toggles the extended messaging panel,
             mobile/tablet expands its groups inline.
             CNCT-6775: the open state is deliberately NOT bound to mobileDrawerState. Binding the
             two made opening the drawer also open (and highlight) the Messages submenu, which read as
             "expanded by default" on tablet. The row now owns its own state and starts collapsed, like
             every other expandable nav item. -->
        <ng-template #messageSlot>
          <custom-messaging-nav [iconOnly]="false"></custom-messaging-nav>
        </ng-template>
        <ng-template #messageSlotIconOnly>
          <custom-messaging-nav [iconOnly]="true"></custom-messaging-nav>
        </ng-template>
      </ng-container>
    </ng-container>
  `,
  styles: [
    `
      /* Settings rail below 'lg' (CNCT-6779). Laid out in flow, so nothing here depends
         on the header's height — which is content-driven (py-4 plus its children). */

      /* Tablet: a docked left column beside the content, inside the same row. */
      .settings-rail-docked {
        width: 272px;
        border-right: 1px solid rgba(var(--color-app-borders), 0.6);
      }

      /* Mobile: a dropdown anchored to its own trigger. No bottom padding on the panel —
         the scroll lives inside it, so the list reaches the bottom edge instead of being
         cut off above a dead band. */
      /* Below the header's own z-12 stacking context, so the notification popover — which
         opens from the bell INSIDE that header and hangs down over this row — paints above
         the trigger rather than behind it. The dropdown this opens still clears the page
         content, which sets no z-index of its own. */
      .settings-rail-mobile {
        z-index: 10;
      }
      .settings-rail-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        max-height: 62vh;
        overflow-y: auto;
        z-index: 2;
        padding: 0.75rem 0.75rem 1rem;
        border-bottom: 1px solid rgba(var(--color-app-borders), 0.6);
        box-shadow: 0 16px 34px rgba(0, 0, 0, 0.2);
      }
      .settings-rail-scrim {
        position: fixed;
        inset: 0;
        z-index: 1;
        background: rgba(0, 0, 0, 0.35);
      }

      .settings-rail-trigger {
        border-bottom: 1px solid rgba(var(--color-app-borders), 0.6);
        color: rgba(var(--color-text-base), 1);
        cursor: pointer;
      }
      .settings-rail-trigger .settings-rail-sep {
        margin: 0 0.35rem;
        font-size: 0.7rem;
        vertical-align: 1px;
      }
      .settings-rail-chev {
        transition: transform 0.18s ease;
      }
      .settings-rail-trigger[aria-expanded='true'] .settings-rail-chev {
        transform: rotate(180deg);
      }

      @media (prefers-reduced-motion: reduce) {
        .settings-rail-chev {
          transition: none;
        }
      }
    `,
  ],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(-80px)' }),
        animate('300ms ease-in-out', style({ transform: 'translateX(0)', width: '*' })),
      ]),
    ]),
  ],
})
export class MainLayoutComponent implements OnInit, AfterViewInit {
  @ViewChild('mobileNavSideOver') mobileNavSideOver!: E11SideOverContainerComponent
  @ViewChild('mainLayoutScrollableElement') mainLayoutScrollableElement!: ElementRef
  @ViewChild('primaryNav') primaryNav?: ElementRef<HTMLElement>

  /**
   * Mobile bottom-tabs — Direction B from the UX spec: 5 fixed tabs (Dashboard,
   * Openings, Engage, Messages, More). "More" opens the mobile drawer with the
   * rest of the rail (Company, Campaigns, Reporting, orphans).
   *
   * Story 2.3.
   */
  mobileTabs5: INavBottomTab[] = [
    { id: 'dashboard', name: 'Dashboard', routeGroup: 'main', icon: 'dashboard' },
    { id: 'openings', name: 'Openings', routeGroup: 'main', icon: 'work' },
    { id: 'engage', name: 'Pipelines', routeGroup: 'main', icon: 'handshake' },
    { id: 'messages', name: 'Messages', routeGroup: 'messaging', icon: 'mail' },
    { id: 'more', name: 'More', routeGroup: 'main', icon: 'more_horiz' },
  ]
  mobileTabs$ = this.configStore.features$.pipe(
    map(features => this.mobileTabs5.filter(tab => tab.id !== 'dashboard' || features?.employerDashboard === true))
  )

  logoUrl = this.appConfigService.config.brand.logoImageLightUrl
  collapsedLogoUrl = this.appConfigService.config.brand.favIconUrl
  mobileDrawerState = false
  tabsConfig$ = this.employerNavigationService.navigationItems$

  userPermissions$ = this.permissionStore.userPermissions$
  pageTitle$ = this.pageTitleService.pageTitle$

  Permission = Permission
  ROUTES = ROUTES

  extendedNavContent$ = this.employerNavigationService.extendedNavContent$
  /** Collapsed → launcher FAB; expanded → the full-width resizable terminal panel. */
  assistantBarExpanded$ = this.employerNavigationService.assistantBarExpanded$
  // Dark-launch flag for the whole assistant feature (terminal + tour + onboarding). OFF hides every
  // assistant surface; ON renders them and the Cnect Plus permission gate then decides composer vs upsell.
  // Default OFF / opt-in: an environment shows the assistant only when its remote features explicitly set
  // employerAssistant:true, so a config that predates the key keeps the assistant hidden.
  assistantEnabled$ = this.configStore.features$.pipe(
    map(features => features?.employerAssistant === true),
    shareReplay({ bufferSize: 1, refCount: true })
  )
  /** Honor the OS reduced-motion preference: disables the assistant dock slide animation. */
  reducedMotion = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  isMobile$ = this.viewportService.belowBreakpoint('lg')

  ExtendedNavId = ExtendedNavId

  /**
   * Panels that coexist with an expanded menu rather than forcing it to icons. For these
   * the collapse is a width decision (see `shouldRenderCollapsed`) and expanding the menu
   * leaves them open. Messaging is absent deliberately: it is a dark drawer that reads as
   * an extension of the nav itself, not a second column beside it.
   */
  private static readonly PANELS_BESIDE_EXPANDED_MENU: ReadonlyArray<ExtendedNavId> = [ExtendedNavId.Settings, ExtendedNavId.Reports]

  private coexistsWithExpandedMenu(id: ExtendedNavId | null | undefined): boolean {
    return !!id && MainLayoutComponent.PANELS_BESIDE_EXPANDED_MENU.includes(id)
  }

  /**
   * Classes for the extended-nav column, as ONE string rather than an ngClass object.
   *
   * An object whose keys share class names is a trap: NgClass adds the truthy key's
   * classes and REMOVES the falsy keys', walking them in object order, so for any class
   * named by more than one key the last key processed decides. Settings and Reports want
   * the same white-with-a-separator treatment, so as an object the Reports branch had its
   * background and border added and then stripped straight back off by the falsy Settings
   * key — the column rendered transparent and separator-less, while Settings, declared
   * last, looked right. Returning a single string makes the winner explicit.
   */
  extendedNavClasses(id: ExtendedNavId | null | undefined): string {
    const rail = 'e11-bg-skin-white e11-border-r e11-border-skin-app-borders'
    switch (id) {
      case ExtendedNavId.Messaging:
        return 'e11-bg-skin-app-bg-dark e11-border-l e11-border-l-skin-grey/30 e11-p-4'
      case ExtendedNavId.Reports:
        // Reports keeps the wrapper padding; the Settings rail supplies its own.
        return `${rail} e11-p-4`
      case ExtendedNavId.Settings:
        return rail
      default:
        return ''
    }
  }

  /** Tier predicates used by the template to place the Settings rail (CNCT-6779). */
  isMobileTier = isMobileTier
  isTabletTier = isTabletTier
  isDesktopTier = isDesktopTier

  /**
   * Streams the current navigation tier (mobile/tablet/small-desktop/desktop/xl)
   * from window resize events. Used to drive tablet-vs-desktop sidebar behavior.
   * Story 2.1 / 2.2.
   */
  navTier$ = fromEvent(window, 'resize').pipe(
    startWith(null),
    map(() => navTierFromWidth(window.innerWidth)),
    map((tier: NavTier) => tier),
    tap(() => (this.viewportWidth = window.innerWidth)),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  /**
   * Latched viewport width for `shouldRenderCollapsed`, which the template calls
   * positionally and so cannot take a stream. Kept in step by the same resize events
   * that drive `navTier$`, which also run change detection, so the method never reads
   * a stale width (CNCT-6779).
   */
  private viewportWidth = window.innerWidth

  /** Mobile-only: whether the Settings dropdown under the top bar is open (CNCT-6779). */
  settingsRailOpen = false

  /**
   * Label of the open settings category, shown on the mobile trigger row so you can
   * see where you are without opening the dropdown. Empty on the hub landing itself.
   */
  settingsRailCategory$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map(event => event.urlAfterRedirects),
    startWith(this.router.url),
    tap(() => (this.settingsRailOpen = false)),
    map(url => this.employerNavigationService.settingsCategories.find(cat => cat.route === url.split('?')[0])?.label ?? ''),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  /**
   * Combined sidebar collapse state. On desktop: respects the user's localStorage
   * preference (default expanded). On tablet: forced collapsed regardless of
   * preference. Story 1.5 / 1.6 / 2.2.
   */
  sidebarCollapsed$ = combineLatest([this.navTier$, this.sidebarPreferences.collapsed$]).pipe(
    map(([tier, pref]) => {
      if (isTabletTier(tier)) return true
      if (isMobileTier(tier)) return false // sidebar hidden on mobile; value moot
      return pref ?? false
    })
  )

  constructor(
    private appConfigService: AppConfigService,
    private employerNavigationService: EmployerNavigationService,
    private permissionStore: PermissionStore,
    private router: Router,
    private store: Store,
    private viewportService: ViewportService,
    private cdr: ChangeDetectorRef,
    private pageTitleService: PageTitleService,
    private sidebarPreferences: SidebarPreferencesService,
    private assistantOnboardingService: AssistantOnboardingService,
    private configStore: ConfigStore
  ) {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationStart)).subscribe({
      next: event => {
        this.mobileDrawerStateChange(false)
        if (event instanceof NavigationEnd) {
          this.resetStaleExtendedNav(event.urlAfterRedirects)
          this.openExtendedNavForUrl(event.urlAfterRedirects)
          this.scrollActiveNavItemIntoView()
        }
      },
    })
  }

  /**
   * The rail's item list is height-bounded (e11-min-h-0 on the e11-nav-left host) so
   * it scrolls rather than pushing past the viewport on short screens. When that
   * happens, bring the active row into view once the group expand transition (150ms)
   * has settled.
   */
  private scrollActiveNavItemIntoView() {
    setTimeout(() => {
      this.primaryNav?.nativeElement.querySelector('a.nav-link.active, a.nav-link-child.active')?.scrollIntoView({ block: 'nearest' })
    }, 200)
  }

  /**
   * `extendedNavContent` is a root singleton that opens the secondary rail (Reports
   * set it in their view activation; Messaging via its toggle; Settings via URL
   * ownership below). Left unattended it sticks as you navigate and forces the primary
   * rail into icon-only mode app-wide. Clear it whenever we land on a URL the currently
   * active panel does not own. Ownership is resolved by the navigation service per
   * `ExtendedNavId` (Reports → /reports; Messaging → none, so it always clears — the
   * legacy behavior; Settings → all catalog routes + /settings), so Reports and
   * Messaging behavior is preserved byte-for-byte.
   */
  private resetStaleExtendedNav(url: string) {
    const active = this.employerNavigationService.extendedNavContent$.value
    if (active && !this.employerNavigationService.isUrlOwnedByExtendedNav(active, url)) {
      this.employerNavigationService.resetExtendedNavContent()
    }
  }

  /**
   * URL-driven activation of the Settings rail. Unlike Reports/Messaging (which open from
   * their own view or toggle), Settings has no view of its own to open from, so this lives
   * here. It fires for the hub landing AND for every hub-owned settings screen, so a
   * refresh, bookmark or shared link onto one arrives with its navigation rather than
   * stranded — the left-nav rows those screens used to have are gone (CNCT-6779). Once
   * open, `resetStaleExtendedNav` keeps it up across catalog routes.
   */
  private openExtendedNavForUrl(url: string) {
    const opensSettingsRail = this.employerNavigationService.shouldOpenSettingsRailForUrl(url)
    if (opensSettingsRail && this.employerNavigationService.extendedNavContent$.value !== ExtendedNavId.Settings) {
      this.employerNavigationService.setExtendedNavContent(ExtendedNavId.Settings)
    }
  }

  ngOnInit(): void {
    // Story 4.7 — offer the onboarding tour once, on a new user's first login (idempotent; gated behind
    // an authenticated current user and the per-user onboarding-offered flag inside the service). The
    // tour ships independently of the assistant dark-launch flag, so this runs unconditionally.
    this.assistantOnboardingService.evaluateFirstLogin()
    // Latched for the hotkey handler below: with the assistant flagged off, ⌘K must fall through to the
    // browser rather than being swallowed by a shortcut whose surface never renders.
    this.assistantEnabled$.subscribe(enabled => (this.assistantHotkeyEnabled = enabled))
  }

  ngAfterViewInit() {
    this.setMainLayoutScrollableElement()
    this.cdr.detectChanges()
  }

  setMainLayoutScrollableElement() {
    this.employerNavigationService.setMainLayoutScrollableElement(this.mainLayoutScrollableElement)
  }

  /**
   * The sidebar renders in collapsed (icon-only) mode when the user collapsed it, or
   * when an open extended-nav panel needs the room.
   *
   * CNCT-6779: the Settings and Reports rails only take the room when the width cannot
   * hold both. 280 + 272 leaves 472px of content at 1024px, which is too little, but
   * 728px at 1280px is fine — so the menu stays expanded from `desktopMin` up and
   * collapses below it, instead of squeezing to icons the moment a rail is opened.
   * Messaging keeps the original always-collapse behaviour.
   *
   * That width rule is a DEFAULT, not a lock: an explicit expand beats it, which is
   * why the stored preference is consulted directly rather than through the template's
   * `collapsedPref`. That value arrives as `pref ?? false`, so it cannot tell "the user
   * expanded this" from "the user never chose", and the two have to behave differently
   * — otherwise expanding below 1280px would be a visible no-op and the rail would have
   * to be dismissed to make the click mean anything.
   */
  shouldRenderCollapsed(collapsedPref: boolean | null, extendedNavContent: ExtendedNavId | null | undefined): boolean {
    if (collapsedPref) return true
    if (!extendedNavContent) return false
    if (!this.coexistsWithExpandedMenu(extendedNavContent)) return true
    if (this.sidebarPreferences.collapsed === false) return false
    return this.viewportWidth < NAV_BREAKPOINTS.desktopMin
  }

  toggleSettingsRail(): void {
    this.settingsRailOpen = !this.settingsRailOpen
  }

  closeSettingsRail(): void {
    this.settingsRailOpen = false
  }

  toggleCollapsed(tier: NavTier): void {
    // Base the toggle on what's actually rendered, not the stored preference alone.
    // An open extended-nav panel forces icon-only mode even when the pref is expanded,
    // so keying off the pref would make the first "Expand" click a no-op (it would flip
    // the pref to collapsed while the panel kept the rail collapsed).
    const current = this.shouldRenderCollapsed(this.sidebarPreferences.collapsed ?? false, this.employerNavigationService.extendedNavContent$.value)
    const next = !current
    // Persist only at desktop+ widths per Story 1.6 AC: 3 (tablet doesn't persist).
    const persist = !isTabletTier(tier) && !isMobileTier(tier)
    this.sidebarPreferences.setCollapsed(next, persist)
    // Expanding dismisses a panel that would otherwise keep the rail collapsed and make
    // the click do nothing. The Settings and Reports rails are exempt (CNCT-6779): an
    // explicit expand beats their width rule, so the menu really does expand and the rail
    // is meant to stay — closing one because someone widened the main menu is a surprise,
    // and neither has a way back short of re-navigating to it.
    if (!next && !this.coexistsWithExpandedMenu(this.employerNavigationService.extendedNavContent$.value)) {
      this.employerNavigationService.resetExtendedNavContent()
    }
  }

  /**
   * "Owned" routes per tab. Tap → primary route. The "More" tab opens the drawer.
   * Active state on a tab matches if the current URL is in its owned set.
   * Stories 2.3 / 2.4.
   */
  private readonly tabOwnedPrefixes: Record<string, string[]> = {
    dashboard: [ROUTES.DASHBOARD, '/home'],
    // /jobs splits between the two tabs rather than belonging to Openings wholesale: the
    // pipeline list and the candidate screens hanging off it are what the Pipelines tab is,
    // and a '/jobs' catch-all here kept Openings lit while you were standing in Pipelines.
    openings: ['/jobs/manage', '/jobs/post', '/jobs/template', '/jobs/profile', '/jobs/settings', '/jobs/promotions'],
    engage: ['/jobs/list', '/jobs/candidates', '/jobs/applications', '/candidate-pool', '/engagement'],
    messages: ['/messages'],
    more: ['/customer', '/customer-clients', '/marketing', '/reports', '/account', '/users', '/notification', '/c', '/settings'],
  }

  isMobileTabActive(tab: INavBottomTab): boolean {
    const url = this.router.url
    const owned = this.tabOwnedPrefixes[tab.id ?? '']
    if (!owned) return false
    return owned.some(prefix => url === prefix || url.startsWith(prefix + '/') || url.startsWith(prefix + '?'))
  }

  onMobileTabTap(tab: INavBottomTab): void {
    if (tab.id === 'more') {
      this.mobileDrawerStateChange(true)
      return
    }
    const primary = this.tabPrimaryRoute(tab.id ?? '')
    if (primary) this.router.navigateByUrl(primary)
  }

  private tabPrimaryRoute(id: string): string | null {
    switch (id) {
      case 'dashboard':
        return ROUTES.DASHBOARD
      case 'openings':
        return ROUTES.JOBS_MANAGE
      // Pipelines is the jobs list, the same destination the rail's Pipelines row and the
      // section tab use — Talent Pool is a tab on that screen, not the tab's landing place.
      case 'engage':
        return ROUTES.JOBS
      case 'messages':
        return '/messages'
      default:
        return null
    }
  }

  /**
   * The side-over overlay is ONE shared surface (`E11SideOverService.overlayToggle`), so
   * `close()` tears down whichever side-over is currently showing — not just this one.
   * Closing when our own drawer is not open therefore shuts someone else's.
   *
   * That is not hypothetical: this runs on every NavigationStart, and the reports filter
   * drawer writes its filters to query params as they initialise, so opening that drawer
   * navigated, which closed it again on the spot. Guarding on our own state keeps this
   * method to the drawer it owns.
   */
  mobileDrawerStateChange(isOpen: boolean) {
    if (!this.mobileNavSideOver) return
    if (!isOpen && !this.mobileDrawerState) return
    this.mobileDrawerState = isOpen
    if (isOpen) {
      this.mobileNavSideOver.open()
      return
    }

    this.mobileNavSideOver.close()
  }

  /**
   * CNCT-6775: tapping outside the nav drawer closes it — the behavior every drawer on
   * tablet/mobile is expected to have, and previously the only ways out were the hamburger or
   * tapping a leaf.
   *
   * e11-side-over renders the backdrop as a bare `<div class="e11-backdrop ...">` with no click
   * handler, and while both e11-side-over and e11-side-over-container accept a
   * `closeOnClickOutside` input, neither template ever reads it — dead config in the library. So
   * the listener lives here rather than an option that would silently do nothing.
   *
   * Matching the backdrop ALONE is not enough: the overlay chrome is wider than the drawer you can
   * see. `size: 'lg'` puts `.e11-side-over-lg { width: 40rem }` on the library's sizing div (a
   * component style, so it outranks the `e11-w-3/4` we pass), while that 3/4 lands on the content
   * div inside it — 480px visible inside a 640px box, plus the wrapper's own `pl-10`. The result is
   * ~200px of transparent, click-absorbing overlay to the right of the drawer that swallowed taps
   * meant for the backdrop. So dismiss on anything inside the overlay that is not the drawer panel
   * itself, as well as the backdrop proper.
   *
   * The hamburger lives outside `.e11-side-over`, so the click that OPENS the drawer can never
   * bubble in here and immediately close it again. Escape still works via the library's handler,
   * and the guard on `mobileDrawerState` leaves other side-overs sharing this backdrop alone.
   */
  @HostListener('document:click', ['$event'])
  onBackdropClick(event: MouseEvent): void {
    if (!this.mobileDrawerState) return
    const target = event.target as HTMLElement | null
    if (!target) return

    const onBackdrop = target.classList.contains('e11-backdrop')
    const inOverlayChrome = !!target.closest('.e11-side-over') && !target.closest('[data-nav-drawer-panel]')

    if (onBackdrop || inOverlayChrome) {
      this.mobileDrawerStateChange(false)
    }
  }

  logout() {
    this.store.dispatch(userActions.OnLogOut())
  }

  expandAssistantBar(): void {
    this.employerNavigationService.setAssistantBarExpanded(true)
  }

  collapseAssistantBar(): void {
    this.employerNavigationService.setAssistantBarExpanded(false)
  }

  /**
   * ⌘K / Ctrl-K expands the collapsed bar from anywhere (once expanded, the composer's own handler takes
   * over to focus the input). Never steals the shortcut while a real input is focused for typing — it
   * only opens the assistant, which is safe from any context.
   */
  private assistantHotkeyEnabled = true

  @HostListener('document:keydown', ['$event'])
  onAssistantHotkey(event: KeyboardEvent): void {
    if (this.assistantHotkeyEnabled && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault()
      this.expandAssistantBar()
    }
  }
}
